import React, { useState, useEffect } from 'react';
import {
  ShoppingBag,
  Package,
  Plus,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Save,
  Edit2,
  Trash2,
  CheckCircle,
  AlertCircle,
  Car,
  User as UserIcon,
  Users,
  Wrench,
  Mail,
  MessageCircle,
  Headphones,
  Menu,
  SquarePen,
  Search,
  Home,
  LogOut,
  Settings,
  Shield,
  UserCheck,
  X,
  MoreVertical,
  BarChart3,
  TrendingUp,
  Tag,
  Plug,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { orderService, OrderResponse, OrderCreateRequest } from '../services/OrderService';
import { useOrder } from '../contexts/OrderContext';
import WorkingOrgSwitcher from '../components/common/WorkingOrgSwitcher';
import { useIsOrgAdmin } from '../hooks/useIsOrgAdmin';
import ConfirmModal from '../components/common/modals/ConfirmModal';
import OrderSetupModal, { OrderSetupData } from '../components/features/orders/OrderSetupModal';
import AssistanceRequestModal from '../components/common/modals/AssistanceRequestModal';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSession } from '../contexts/SessionContext';
import ChatService from '../services/ChatService';
import LanguageSelector from '../components/common/LanguageSelector';
import VersionIndicator from '../components/common/VersionIndicator';
import { useOrderCreation } from '../hooks/useOrderCreation';
import CataxLogo from '../assets/CataxLogo';
import CataxIcon from '../assets/CataxIcon';
import UserProfileModal from '../components/common/modals/UserProfileModal';

interface OrdersPanelStaticProps {
  newOrderCreated: (data: OrderResponse) => void;
  clearChat: () => void;
  onOrdersUpdate?: (ordersCount: number) => void;
  hasNewMessagesForOrder?: (orderId: number) => boolean;
  isMinimized?: boolean;
  onToggleMinimize?: () => void;
  triggerCreateOrder?: boolean;
}

// A single sidebar nav row. Active rows get the lilac highlight + indigo text/icon
// (matches the app's accent), inactive rows are neutral with a light hover.
const ACTIVE_BG = '#eeecff';
const ACTIVE_FG = '#4f46e5';
const NavRow: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  onClick: () => void;
}> = ({ icon: Icon, label, active = false, onClick }) => (
  <button
    onClick={onClick}
    aria-current={active ? 'page' : undefined}
    className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-xl transition-colors font-jakarta ${
      active ? 'font-semibold' : 'text-[#201e1a] hover:bg-gray-100 font-medium'
    }`}
    style={active ? { backgroundColor: ACTIVE_BG, color: ACTIVE_FG } : undefined}
  >
    <Icon className="w-4 h-4 flex-shrink-0" />
    <span className="text-sm">{label}</span>
  </button>
);

// Icon-only nav button for the collapsed (minimized) sidebar.
const MiniNavIcon: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  active?: boolean;
  onClick: () => void;
}> = ({ icon: Icon, title, active = false, onClick }) => (
  <button
    onClick={onClick}
    aria-current={active ? 'page' : undefined}
    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
      active ? '' : 'bg-gray-100 hover:bg-gray-200 text-[#201e1a]'
    }`}
    style={active ? { backgroundColor: ACTIVE_BG, color: ACTIVE_FG } : undefined}
    title={title}
  >
    <Icon className="w-5 h-5" />
  </button>
);

const OrdersPanelStatic: React.FC<OrdersPanelStaticProps> = ({
  newOrderCreated,
  clearChat,
  onOrdersUpdate,
  hasNewMessagesForOrder,
  isMinimized = false,
  onToggleMinimize,
  triggerCreateOrder = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { getActiveOrderId, setActiveOrderId, registerRefreshCallback, removeActiveOrderId, setOrderObject } = useOrder();
  const { isCreating: isCreatingNew, error: orderCreationError, createOrder, clearError } = useOrderCreation();
  const [error, setError] = useState<string | null>(null);
  const [showOrderSetupModal, setShowOrderSetupModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<number | null>(null);
  const [showAssistanceModal, setShowAssistanceModal] = useState(false);
  const [assistanceOrderId, setAssistanceOrderId] = useState<number | null>(null);
  const [assistanceOrderName, setAssistanceOrderName] = useState<string>('');
  const [assistanceSessionId, setAssistanceSessionId] = useState<string>('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [orderToClose, setOrderToClose] = useState<number | null>(null);
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState<OrderResponse | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const { currentUser, logout, isImpersonating, impersonatedUser, originalAdmin, stopImpersonation } = useAuth();
  const isOrgAdmin = useIsOrgAdmin();
  const isROIAllowed = currentUser?.roi_calculator_enabled ?? false;
  const roiNavPath = (!currentUser?.roi_b2b_enabled && currentUser?.roi_b2c_enabled)
    ? '/roi-calculator/b2c'
    : '/roi-calculator/b2b';
  const { currentLanguage } = useLanguage();
  const { setActiveSessionId } = useSession();
  const displayUser = isImpersonating && impersonatedUser ? impersonatedUser : currentUser;

  // Handle trigger to open create order modal
  useEffect(() => {
    if (triggerCreateOrder) {
      setShowOrderSetupModal(true);
    }
  }, [triggerCreateOrder]);

  // Active-route detection for the sidebar nav highlight. `exact` matches the
  // pathname exactly; otherwise a section prefix (e.g. /orders, /orders/42) matches.
  const isPathActive = (path: string, exact = false) =>
    exact ? location.pathname === path : location.pathname === path || location.pathname.startsWith(`${path}/`);


  const handleOrderSetupSubmit = async (orderSetupData: OrderSetupData) => {
    // Clear any previous errors
    setError(null);
    clearError();

    // Create order using shared hook
    const createdOrder = await createOrder({
      name: orderSetupData.name,
      vin: orderSetupData.vin,
      buyer: orderSetupData.buyer,
      service_type: orderSetupData.service_type
    });

    // If order was created successfully
    if (createdOrder) {
      setShowOrderSetupModal(false);

      // Notify parent component about new order
      newOrderCreated(createdOrder);
    } else {
      // If there was an error, copy it to local error state for display
      if (orderCreationError) {
        setError(orderCreationError);
      }
    }
  };

  const deleteOrder = async (orderId: number) => {
    setOrderToDelete(orderId);
    setShowDeleteConfirm(true);
    localStorage.removeItem('activeOrderId');
  };

  const markOrderComplete = async (orderId: number) => {
    setOrderToClose(orderId);
    setShowCloseConfirm(true);
  };

  const handleConfirmClose = async () => {
    if (!orderToClose) return;

    try {
      await orderService.updateOrderStatus(orderToClose, 'submitted');

      // Clear chat messages if closing the active order
      if (getActiveOrderId() === orderToClose) {
        clearChat();
      }
    } catch (err) {
      setError(t('orders.panel.errors.failedToSubmit'));
      console.error('Error updating order status:', err);
    } finally {
      setOrderToClose(null);
      setShowCloseConfirm(false);
    }
  };

  const handleEditOrderSubmit = async (orderSetupData: OrderSetupData) => {
    if (!orderToEdit) return;

    try {
      // Update the order with new details
      const updateData = {
        name: orderSetupData.name,
        vin: orderSetupData.vin,
        buyer: orderSetupData.buyer || "",
        service_type: orderSetupData.service_type || "",
        parts_in_order: orderToEdit.parts_in_order // Keep existing parts
      };

      await orderService.updateOrder(orderToEdit.id, updateData);
      setShowEditOrderModal(false);
      setOrderToEdit(null);
    } catch (err) {
      setError(t('orders.panel.errors.failedToUpdate'));
      console.error('Error updating order:', err);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const navigateToSettings = () => {
    navigate('/settings');
    setIsUserMenuOpen(false);
  };

  const openProfileModal = () => {
    setIsProfileModalOpen(true);
    setIsUserMenuOpen(false);
  };


  const navigateToAdmin = () => {
    navigate('/admin');
    setIsUserMenuOpen(false);
  };

  const handleStopImpersonation = () => {
    stopImpersonation();
    navigate('/admin/users');
  };


  const handleToggleMinimize = () => {
    if (onToggleMinimize) {
      onToggleMinimize();
      // Save the new state to localStorage
      localStorage.setItem('ordersPanelMinimized', (!isMinimized).toString());
    }
  };

  return (
    <div className={`h-full flex flex-col bg-white border-r ${isMinimized ? 'w-20' : ''} relative z-50 overflow-visible`} style={{ borderColor: '#efebe2' }}>
      {/* Minimized State */}
      {isMinimized ? (
        <div
          key="minimized"
          className="flex flex-col items-center pt-4 pb-4 h-full overflow-visible"
        >
          {/* Logo + Expand Arrow Row */}
          <div className="relative flex items-center justify-center w-full mb-4 px-2">
            <button
              onClick={() => navigate('/')}
              className="flex-shrink-0"
              title="Cata.ai"
            >
              <CataxIcon className="w-7 h-7" />
            </button>
            <button
              onClick={handleToggleMinimize}
              className="absolute flex items-center justify-center rounded-lg transition-colors hover:opacity-70 z-20"
              style={{ backgroundColor: '#efebe2', width: 25, height: 32, right: -13 }}
              title={t('orders.panel.expandPanel')}
            >
              <ChevronRight className="w-3.5 h-3.5 text-[#201e1a]" />
            </button>
          </div>

          {/* New Chat — same primary action as the expanded "New chat" button,
              kept visible when the sidebar is collapsed. */}
          <MiniNavIcon
            icon={SquarePen}
            title={t('homepage.buttons.startNewChat', { defaultValue: 'New chat' })}
            onClick={() => navigate('/')}
          />
          {/* Order notifications bell (OrderNotificationsBell) is hidden for
              now — mount it here again when notifications ship. */}
          {/* Divider */}
          <div className="w-8 border-t my-3" style={{ borderColor: '#efebe2' }} />

          {/* Navigation Icons */}
          <div className="flex flex-col items-center gap-3">
            <MiniNavIcon
              icon={ShoppingBag}
              title={t('header.orders', { defaultValue: 'Orders' })}
              active={isPathActive('/orders')}
              onClick={() => navigate('/orders')}
            />
            {isROIAllowed && (
              <MiniNavIcon
                icon={TrendingUp}
                title={t('header.roiCalculator', { defaultValue: 'ROI Calculator' })}
                active={location.pathname.startsWith('/roi-calculator')}
                onClick={() => navigate(roiNavPath)}
              />
            )}
            {isOrgAdmin && (
              <MiniNavIcon
                icon={BarChart3}
                title={t('header.usagePlan', { defaultValue: 'Usage & Plan' })}
                active={isPathActive('/usage', true)}
                onClick={() => navigate('/usage')}
              />
            )}
            <MiniNavIcon
              icon={Users}
              title={t('header.team', { defaultValue: 'Team' })}
              active={isPathActive('/team')}
              onClick={() => navigate('/team')}
            />
            <MiniNavIcon
              icon={Settings}
              title={t('header.organization', { defaultValue: 'Organization' })}
              active={isPathActive('/organization')}
              onClick={() => navigate('/organization')}
            />
            <MiniNavIcon
              icon={Plug}
              title={t('header.connections', { defaultValue: 'Connections' })}
              active={isPathActive('/settings')}
              onClick={navigateToSettings}
            />
          </div>

          {/* User Avatar - at bottom */}
          <div className="mt-auto flex flex-col items-center pb-2 overflow-visible">
            {currentUser ? (
              <div className="relative overflow-visible">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="transition-opacity hover:opacity-80"
                  title={displayUser?.displayName || displayUser?.email || 'User'}
                >
                  <div
                    className="w-9 h-9 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: '#ffcdce' }}
                  >
                    <span className="text-sm font-bold text-[#201e1a] leading-none">
                      {(displayUser?.name || displayUser?.email || 'U')
                        .split(' ')
                        .map((n: string) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                    </span>
                  </div>
                  {isImpersonating && (
                    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full border border-white" />
                  )}
                </button>

                {/* User Menu Popup - Minimized */}
                {isUserMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-[9999]"
                      onClick={() => setIsUserMenuOpen(false)}
                    />
                    <div className="absolute bottom-0 left-full ml-2 bg-white rounded-lg shadow-xl border border-gray-200 z-[10000] overflow-hidden min-w-[200px]">
                      <div className="p-2">
                        <div className="px-3 py-2 border-b border-gray-200 mb-2">
                          {/* <p className="text-profile-name font-jakarta text-gray-900 truncate">
                            {displayUser?.displayName || 'User'}
                          </p> */}
                          <p className="text-profile-email font-jakarta text-gray-600 truncate">
                            {displayUser?.email}
                          </p>
                        </div>
                        <button
                          onClick={openProfileModal}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-nav-link font-jakarta"
                        >
                          <UserIcon className="w-4 h-4" />
                          {t('header.profile', 'Profile')}
                        </button>
                        {currentUser?.role === 'admin' && (
                          <button
                            onClick={navigateToAdmin}
                            className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-nav-link font-jakarta"
                          >
                            <Shield className="w-4 h-4" />
                            {t('header.adminPanel', { defaultValue: 'Admin Panel' })}
                          </button>
                        )}
                        <WorkingOrgSwitcher />
                        <div className="h-px bg-gray-200 my-2"></div>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-red-50 rounded-lg transition-colors text-nav-link font-jakarta"
                        >
                          <LogOut className="w-4 h-4" />
                          {t('header.signOut')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="w-9 h-9 bg-gray-100 rounded-2xl flex items-center justify-center hover:bg-gray-200 transition-colors"
                title="Sign In"
              >
                <UserIcon className="w-4 h-4 text-[#201e1a]" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Logo and Top Actions Panel */}
          <div className="px-5 pt-5 pb-4 flex-shrink-0 relative flex items-center">
            {/* Logo + org name - Expanded */}
            <button
              onClick={() => navigate('/')}
              className="flex flex-col items-start gap-0.5 min-w-0"
              aria-label="Home"
            >
              <CataxLogo className="h-8" />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 font-jakarta truncate max-w-full">
                {t('sidebar.tagline', { defaultValue: 'Agentic assistance' })}
              </span>
            </button>

            {/* Collapse button - floats on the right edge of the sidebar */}
            <button
              onClick={handleToggleMinimize}
              className="absolute flex items-center justify-center rounded-lg transition-colors hover:opacity-70 z-20"
              style={{ backgroundColor: '#efebe2', width: 25, height: 32, right: -15 }}
              title={t('orders.panel.minimizePanel')}
            >
              <ChevronLeft className="w-4 h-4 text-[#201e1a]" />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border-b border-red-200 p-3 flex items-center gap-2 mx-4 mb-2 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* New Chat Button */}
          <div className="px-4 pb-3 flex-shrink-0">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-3 px-4 py-2.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors text-[#201e1a] text-sm font-medium font-jakarta w-full"
              title={t('homepage.buttons.startNewChat', { defaultValue: 'New chat' })}
            >
              <SquarePen className="w-4 h-4 flex-shrink-0" />
              <span>{t('homepage.buttons.startNewChat', { defaultValue: 'New chat' })}</span>
            </button>
          </div>

          {/* Divider */}
          <div className="mx-4 mb-4 border-t" style={{ borderColor: '#efebe2' }} />

          {/* Nav + Content - Scrollable area */}
          <div className="flex-1 overflow-y-auto px-4 pt-1">
            {/* Impersonation Banner */}
            {isImpersonating && impersonatedUser && originalAdmin && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <UserCheck className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span className="text-xs font-medium text-amber-900">
                    Impersonating: <strong>{impersonatedUser.displayName || impersonatedUser.email}</strong>
                  </span>
                </div>
                <button
                  onClick={handleStopImpersonation}
                  className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-2 py-1.5 rounded-lg transition-colors text-xs font-medium"
                >
                  <X className="w-3 h-3" />
                  Stop Impersonating
                </button>
              </div>
            )}

            {/* Nav items — grouped into WORKSPACE and ACCOUNT sections. */}
            {/* WORKSPACE */}
            <p className="px-3 pt-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 font-jakarta">
              {t('sidebar.workspace', { defaultValue: 'Workspace' })}
            </p>
            <div className="space-y-1 mb-2">
              <NavRow
                icon={ShoppingBag}
                label={t('header.orders', { defaultValue: 'Orders' })}
                active={isPathActive('/orders')}
                onClick={() => navigate('/orders')}
              />
              {isROIAllowed && (
                <NavRow
                  icon={TrendingUp}
                  label={t('header.roiCalculator', { defaultValue: 'ROI Calculator' })}
                  active={location.pathname.startsWith('/roi-calculator')}
                  onClick={() => navigate(roiNavPath)}
                />
              )}
            </div>

            {/* ACCOUNT */}
            <p className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400 font-jakarta">
              {t('sidebar.account', { defaultValue: 'Account' })}
            </p>
            <div className="space-y-1">
              {isOrgAdmin && (
                <NavRow
                  icon={BarChart3}
                  label={t('header.usagePlan', { defaultValue: 'Usage & Plan' })}
                  active={isPathActive('/usage', true)}
                  onClick={() => navigate('/usage')}
                />
              )}
              <NavRow
                icon={Users}
                label={t('header.team', { defaultValue: 'Team' })}
                active={isPathActive('/team')}
                onClick={() => navigate('/team')}
              />
              <NavRow
                icon={Settings}
                label={t('header.organization', { defaultValue: 'Organization' })}
                active={isPathActive('/organization')}
                onClick={() => navigate('/organization')}
              />
              <NavRow
                icon={Plug}
                label={t('header.connections', { defaultValue: 'Connections' })}
                active={isPathActive('/settings')}
                onClick={navigateToSettings}
              />
            </div>
          </div>

          {/* User Profile Section - Sticky at bottom */}
          <div className="flex-shrink-0 border-t mt-auto bg-white" style={{ borderColor: '#efebe2' }}>
            {/* User Menu */}
            {currentUser ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="w-full flex items-center gap-3 px-4 py-4 hover:bg-gray-50 transition-colors"
                >
                  {displayUser?.picture ? (
                    <img
                      src={displayUser.picture}
                      alt={displayUser.displayName || 'User'}
                      className="w-9 h-9 flex-shrink-0"
                      style={{ borderRadius: 20 }}
                    />
                  ) : (
                    <div
                      className="w-9 h-9 flex-shrink-0 flex items-center justify-center"
                      style={{ backgroundColor: '#ffcdce', borderRadius: 20 }}
                    >
                      <span className="text-sm font-bold text-[#201e1a] leading-none">
                        {(displayUser?.name || displayUser?.email || 'U')
                          .split(' ')
                          .map((n: string) => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-xs font-jakarta truncate leading-snug" style={{ color: '#999999' }}>
                      {displayUser?.email}
                    </p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {isUserMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsUserMenuOpen(false)}
                    />
                    <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
                      <div className="p-2">
                        <button
                          onClick={openProfileModal}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-nav-link font-jakarta"
                        >
                          <UserIcon className="w-4 h-4" />
                          {t('header.profile', 'Profile')}
                        </button>

                        {currentUser?.role === 'admin' && (
                          <button
                            onClick={navigateToAdmin}
                            className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-nav-link font-jakarta"
                          >
                            <Shield className="w-4 h-4" />
                            {t('header.adminPanel', { defaultValue: 'Admin Panel' })}
                          </button>
                        )}
                        <WorkingOrgSwitcher />

                        <div className="h-px bg-gray-200 my-2"></div>

                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left text-gray-700 hover:bg-red-50 rounded-lg transition-colors text-nav-link font-jakarta"
                        >
                          <LogOut className="w-4 h-4" />
                          {t('header.signOut')}
                        </button>
                      </div>

                      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                        <VersionIndicator className="text-gray-400" size="small" />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium px-4 py-2 rounded-lg transition-colors text-sm"
              >
                Sign In
              </button>
            )}
          </div>

          {/* Image Modal */}
          {selectedImage && (
            <div
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedImage(null)}
            >
              <div className="relative max-w-4xl max-h-full">
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-all duration-200 p-1 rounded-lg hover:bg-white/10"
                  title={t('orders.panel.close')}
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <img
                  src={selectedImage}
                  alt="Part"
                  className="max-w-full max-h-[90vh] object-contain rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          <ConfirmModal
            isOpen={showDeleteConfirm}
            onClose={() => {
              setShowDeleteConfirm(false);
              setOrderToDelete(null);
            }}
            onConfirm={() => { }}
            title={t('orders.panel.deleteOrderTitle')}
            message={t('orders.panel.deleteOrderMessage')}
            confirmText={t('orders.panel.confirmDelete')}
            cancelText={t('orders.panel.cancelDelete')}
            confirmButtonClass="bg-red-500 hover:bg-red-600"
          />

          {/* Close Order Confirmation Modal */}
          <ConfirmModal
            isOpen={showCloseConfirm}
            onClose={() => {
              setShowCloseConfirm(false);
              setOrderToClose(null);
            }}
            onConfirm={handleConfirmClose}
            title={t('orders.panel.closeOrderTitle')}
            message={t('orders.panel.closeOrderMessage')}
            confirmText={t('orders.panel.confirmClose')}
            cancelText={t('orders.panel.cancelClose')}
            confirmButtonClass="bg-green-500 hover:bg-green-600"
          />
        </>
      )}

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      {/* Order Setup Modal - Rendered outside conditional to be available in both states */}
      <OrderSetupModal
        isOpen={showOrderSetupModal}
        onClose={() => setShowOrderSetupModal(false)}
        onSubmit={handleOrderSetupSubmit}
        isCreating={isCreatingNew}
      />

      {/* Edit Order Modal */}
      {orderToEdit && (
        <OrderSetupModal
          isOpen={showEditOrderModal}
          onClose={() => {
            setShowEditOrderModal(false);
            setOrderToEdit(null);
          }}
          onSubmit={handleEditOrderSubmit}
          isCreating={false}
          initialData={{
            name: orderToEdit.name,
            vin: orderToEdit.vin || '',
            buyer: orderToEdit.buyer || '',
            service_type: orderToEdit.service_type || ''
          }}
          isEditMode={true}
        />
      )}

      {/* Assistance Request Modal */}
      {assistanceOrderId && (
        <AssistanceRequestModal
          isOpen={showAssistanceModal}
          onClose={() => {
            setShowAssistanceModal(false);
            setAssistanceOrderId(null);
            setAssistanceOrderName('');
            setAssistanceSessionId('');
          }}
          orderId={assistanceOrderId}
          sessionId={assistanceSessionId}
          orderName={assistanceOrderName}
        />
      )}
    </div>
  );
};

export default OrdersPanelStatic;
